
import { useA2AProtocol } from '@/hooks/useA2AProtocol';
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useMCPToolAgent } from './useMCPToolAgent';
import { OrchestratorAgentResponse, OrchestratorResponseMetadata } from '@/components/calendar/types/orchestration';
import { detectAcknowledgment, AcknowledgmentResult } from '@/utils/orchestration/acknowledgmentDetector';
import { MCP_TOOL_AGENTS } from '@/utils/orchestration/mcp/mcpAgent.types';
import { invokeMCPAgent } from '@/utils/orchestration/mcp/invokeMCPAgent';
import { invokeSupabaseOrchestrator } from '@/utils/orchestration/supabase/invokeSupabaseOrchestrator'; // Updated import

interface OrchestratorRequest {
  targetAgent: string;
  subIntent: string;
  eventData?: any;
  contextData?: any;
  priority?: 'high' | 'normal' | 'low';
}

export const useOrchestratorAgent = () => {
  const { sendMessage } = useA2AProtocol();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const microsoftExchange = useMCPToolAgent('microsoft-exchange');
  const linkedin = useMCPToolAgent('linkedin');
  const elevenlabs = useMCPToolAgent('elevenlabs');
  
  const mcpHooks = { microsoftExchange, linkedin, elevenlabs };

  const invokeAgent = useCallback(async (request: OrchestratorRequest): Promise<OrchestratorAgentResponse> => {
    const { targetAgent, subIntent, eventData, contextData, priority = 'normal' } = request;
    
    try {
      setIsProcessing(true);
      
      console.log(`🎯 Enhanced Orchestrator: Invoking ${targetAgent} with intelligent analysis`);
      
      const ackResult: AcknowledgmentResult = detectAcknowledgment(subIntent);
      
      if (ackResult.isAcknowledgment) {
        console.log(`✅ Detected ${ackResult.type} acknowledgment, proceeding with enhanced workflow`);
        
        // Enhanced A2A message for acknowledgments
        const response = await sendMessage({
          content: `Processing acknowledgment with enhanced A2A orchestration: ${subIntent}`,
          toAgent: 'enhanced-executive-agent', 
          metadata: {
            targetAgent, 
            messageType: 'ACKNOWLEDGEMENT',
            acknowledgementType: ackResult.type,
            intent: 'processAcknowledgment', 
            priority: 'high', 
            contextData,
            enhanced_orchestration: true,
            a2a_protocol_version: '2.0'
          }
        });
        return response as OrchestratorAgentResponse;
      }
      
      if (MCP_TOOL_AGENTS.includes(targetAgent as any)) {
        console.log(`🔧 Routing to MCP agent: ${targetAgent}`);
        return await invokeMCPAgent(
          targetAgent as typeof MCP_TOOL_AGENTS[number],
          subIntent,
          eventData,
          mcpHooks
        );
      } else {
        console.log(`🧠 Routing to enhanced Supabase orchestrator: ${targetAgent}`);
        
        // Enhanced orchestrator invocation with intelligent analysis
        return await invokeSupabaseOrchestrator({
          targetAgent: targetAgent === 'brig-ai' ? 'executive-agent' : targetAgent,
          subIntent,
          eventData: {
            ...eventData,
            enhanced_orchestration: true,
            intelligent_analysis: true,
            context_propagation: true
          },
          contextData,
          priority,
        });
      }
    } catch (error) {
      console.error(`❌ Critical error in enhanced orchestrator for ${targetAgent}:`, error);
      toast.error(`An unexpected error occurred while trying to reach ${targetAgent}.`);
      
      let errorMessage = "An unknown error occurred";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      const criticalErrorMetadata: OrchestratorResponseMetadata = {
        status: 'error',
        error: errorMessage,
        targetAgent: targetAgent,
        enhanced_orchestration: true,
        fallback_used: true
      };
      
      return {
        id: `enhanced-err-${Date.now()}`,
        content: `Enhanced orchestration error: ${errorMessage}. The system attempted intelligent recovery but was unable to complete the request.`,
        metadata: criticalErrorMetadata,
      };
    } finally {
      setIsProcessing(false);
    }
  }, [sendMessage, setIsProcessing, mcpHooks]);
  
  return {
    invokeAgent,
    isProcessing,
    detectAcknowledgment 
  };
};
